import { useState, useEffect, useRef } from "react";
import { DEMO_VIDEO_URL } from "../config/env";
import { styles } from "../styles/appStyles";

// Truthful product-capability copy for the social-proof marquee. NOTE: the original prompt
// suggested press/metric claims ("Featured on Real Estate Tech Daily", "50,000+ listings
// generated", "Trusted by listing agents") — omitted here because they are unverified on a
// live revenue site. Swap in real numbers/press only once confirmed.
const MARQUEE_ITEMS = [
  "AI listings in seconds",
  "Live RentCast comps",
  "Lead follow-up emails",
  "Contract summaries in plain English",
  "CMA reports that win listings",
  "Built for real estate agents",
  "Powered by OpenAI + RentCast", // model is gpt-4o-mini in prod — say "OpenAI", not "GPT-4"
];

const DEMO_VIDEO_ID = (DEMO_VIDEO_URL.match(/embed\/([\w-]+)/) || [])[1] || "";

export default function LandingPage({ onGoToAuth, onTryDemo }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  // Click-to-load video facade: show the YouTube thumbnail + a play button, and only mount the
  // (privacy-friendly youtube-nocookie) iframe once the user clicks. Avoids eagerly loading the
  // full YouTube player JS + doubleclick/ads + a third-party cookie on every landing-page view.
  const [videoPlaying, setVideoPlaying] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll-triggered reveals for the feature cards (fires once each). Falls back to fully visible
  // if IntersectionObserver is unavailable, so nothing ever stays hidden.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const els = Array.from(document.querySelectorAll(".reveal-on-scroll"));
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("is-revealed")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-revealed"); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // "Strata" interaction layer — cursor-follow glow + card parallax + magnetic CTAs.
  // One rAF-throttled mousemove on the hero, writing CSS variables (no React re-renders).
  // The glow moves via a compositor-only transform + opacity (no per-frame repaint); cards use
  // translate3d. Hard-gated OFF for touch + prefers-reduced-motion, and the gate is LIVE — it
  // attaches/detaches if the user toggles reduced-motion or pointer capability mid-session.
  const heroRef = useRef(null);
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof window === "undefined" || !window.matchMedia) return;
    const fineMq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");

    const magnets = Array.from(hero.querySelectorAll(".lp-magnetic"));
    let rect = hero.getBoundingClientRect();
    let mRects = magnets.map((m) => m.getBoundingClientRect());
    const measure = () => {
      rect = hero.getBoundingClientRect();
      mRects = magnets.map((m) => m.getBoundingClientRect());
    };

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    let raf = 0;
    let mRaf = 0;
    let px = 0;
    let py = 0;
    const apply = () => {
      raf = 0;
      hero.style.setProperty("--glow-x", px - rect.left + "px");
      hero.style.setProperty("--glow-y", py - rect.top + "px");
      hero.style.setProperty("--glow-o", "1");
      const nx = clamp((px - (rect.left + rect.width / 2)) / (rect.width / 2 || 1), -1, 1);
      const ny = clamp((py - (rect.top + rect.height / 2)) / (rect.height / 2 || 1), -1, 1);
      hero.style.setProperty("--par-x", nx.toFixed(3));
      hero.style.setProperty("--par-y", ny.toFixed(3));
      magnets.forEach((m, i) => {
        const r = mRects[i];
        if (!r) return;
        const dx = px - (r.left + r.width / 2);
        const dy = py - (r.top + r.height / 2);
        if (Math.hypot(dx, dy) < 90) {
          m.style.setProperty("--mag-x", clamp(dx * 0.3, -8, 8).toFixed(1) + "px");
          m.style.setProperty("--mag-y", clamp(dy * 0.3, -8, 8).toFixed(1) + "px");
        } else {
          m.style.setProperty("--mag-x", "0px");
          m.style.setProperty("--mag-y", "0px");
        }
      });
    };
    const onMove = (e) => {
      px = e.clientX;
      py = e.clientY;
      if (!raf) raf = window.requestAnimationFrame(apply);
    };
    const onLeave = () => {
      if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
      hero.style.setProperty("--glow-o", "0");
      hero.style.setProperty("--par-x", "0");
      hero.style.setProperty("--par-y", "0");
      magnets.forEach((m) => {
        m.style.setProperty("--mag-x", "0px");
        m.style.setProperty("--mag-y", "0px");
      });
    };
    // rAF-coalesce re-measurement so a high-frequency scroll doesn't force a reflow per event.
    const scheduleMeasure = () => {
      if (!mRaf) mRaf = window.requestAnimationFrame(() => { mRaf = 0; measure(); });
    };

    let attached = false;
    const attach = () => {
      if (attached) return;
      attached = true;
      measure();
      hero.addEventListener("mousemove", onMove, { passive: true });
      hero.addEventListener("mouseenter", scheduleMeasure, { passive: true });
      hero.addEventListener("mouseleave", onLeave, { passive: true });
      window.addEventListener("scroll", scheduleMeasure, { passive: true });
      window.addEventListener("resize", scheduleMeasure, { passive: true });
    };
    const detach = () => {
      if (!attached) return;
      attached = false;
      hero.removeEventListener("mousemove", onMove);
      hero.removeEventListener("mouseenter", scheduleMeasure);
      hero.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", scheduleMeasure);
      window.removeEventListener("resize", scheduleMeasure);
      onLeave();
    };
    const sync = () => {
      if (fineMq.matches && !reduceMq.matches) attach();
      else detach();
    };
    sync();
    fineMq.addEventListener("change", sync);
    reduceMq.addEventListener("change", sync);
    return () => {
      fineMq.removeEventListener("change", sync);
      reduceMq.removeEventListener("change", sync);
      detach();
      if (raf) window.cancelAnimationFrame(raf);
      if (mRaf) window.cancelAnimationFrame(mRaf);
    };
  }, []);

  const FEATURES = [
    { icon: "🏠", title: "MLS Listing Generator", desc: "Professional copy in seconds" },
    { icon: "📧", title: "Lead Follow-Up Emails", desc: "3 personalized emails per lead" },
    { icon: "📋", title: "Contract Summarizer", desc: "Breaks down contracts into clear, simple language" },
    { icon: "🏡", title: "Open House Follow-Up", desc: "Cycle through every visitor fast" },
    { icon: "📊", title: "Seller Update Emails", desc: "Weekly updates that retain clients" },
    { icon: "👤", title: "Agent Bio Generator", desc: "Full and short bios in one click" },
    { icon: "📈", title: "CMA Generator", desc: "Win more listing presentations" },
    { icon: "🏠", title: "Property Info", desc: "Look up any property and save details instantly" },
    { icon: "👥", title: "Contacts & CRM", desc: "Manage leads, import CSV, track every client" },
    { icon: "📤", title: "Email Export", desc: "Send via Gmail, Outlook, or any email client" },
  ];

  const startSignup = (plan) => {
    if (plan) {
      try { localStorage.setItem("pitcherly_selected_plan", plan); } catch {}
    }
    onGoToAuth("signup");
  };

  const scrollToPricing = () => {
    const el = document.getElementById("landing-pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Clicking the wordmark goes "home" — i.e. back to the top/hero of the landing page.
  // (LandingPage only renders when logged out, so this is the logged-out home by construction.)
  const goHome = () => {
    try { window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch { window.scrollTo(0, 0); }
  };

  return (
    <>
      <style>{styles}</style>
      <style>{`
        .lp-nav-btn { font-family: 'Geist', sans-serif; cursor: pointer; border-radius: 8px; font-weight: 600; transition: all 0.15s; }
        .lp-nav-btn:hover { transform: translateY(-1px); }
        .lp-logo-btn { transition: opacity 0.15s var(--ease-out-soft), transform 0.15s var(--ease-out-soft); }
        .lp-logo-btn:hover { opacity: 0.78; }
        .lp-logo-btn:active { transform: scale(0.97); }
        .lp-cta-primary { background: var(--accent); color: #0d0d0d; border: none; padding: 12px 22px; font-size: 14px; }
        .lp-cta-primary:hover { background: var(--accent2); }
        .lp-cta-ghost { background: transparent; color: var(--text); border: 1px solid var(--border); padding: 12px 18px; font-size: 14px; }
        .lp-cta-ghost:hover { border-color: var(--accent); color: var(--accent); }
        .lp-cta-link { background: transparent; color: var(--text2); border: none; padding: 12px 14px; font-size: 14px; }
        .lp-cta-link:hover { color: var(--accent); }
        .lp-feature { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px; transition: border-color 0.15s, transform 0.15s var(--ease-out-soft); }
        .lp-feature:hover { border-color: var(--accent); transform: translateY(-2px); }
        @keyframes lpFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .lp-hero-stagger > * { animation: lpFadeUp 0.55s var(--ease-out-soft) both; }
        .lp-hero-stagger > *:nth-child(1) { animation-delay: 0.05s; }
        .lp-hero-stagger > *:nth-child(2) { animation-delay: 0.15s; }
        .lp-hero-stagger > *:nth-child(3) { animation-delay: 0.25s; }
        .lp-hero-stagger > *:nth-child(4) { animation-delay: 0.35s; }
        .lp-hero-stagger > *:nth-child(5) { animation-delay: 0.45s; }
        .lp-magnetic { transition: transform 0.18s var(--ease-out-soft); }
        .lp-magnetic, .lp-magnetic:hover { transform: translate(var(--mag-x, 0px), var(--mag-y, 0px)); }
        .lp-magnetic:active { transform: translate(var(--mag-x, 0px), var(--mag-y, 0px)) scale(0.97); }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: "var(--bg)",
        color: "var(--text)",
        fontFamily: "'Geist', sans-serif",
      }}>
        <a href="#lp-main" className="skip-link">Skip to main content</a>
        {/* STICKY NAV */}
        <nav style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(15, 15, 15, 0.85)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          padding: isMobile ? "12px 16px 12px 64px" : "14px 32px 14px 80px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}>
          <button
            type="button"
            onClick={goHome}
            aria-label="Pitcherly — back to top"
            className="lp-logo-btn"
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: isMobile ? 22 : 26,
              fontWeight: 500,
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              color: "var(--text)",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            Pitcher<em style={{ color: "var(--accent)", fontStyle: "italic" }}>ly</em>
          </button>
          <div style={{ display: "flex", gap: isMobile ? 6 : 10, alignItems: "center" }}>
            {!isMobile && (
              <button className="lp-nav-btn lp-cta-link" onClick={onTryDemo}>Try Demo</button>
            )}
            <button className="lp-nav-btn lp-cta-ghost" onClick={() => onGoToAuth("login")} style={isMobile ? { padding: "8px 12px", fontSize: 13 } : {}}>Log In</button>
            <button className="lp-nav-btn lp-cta-primary" onClick={() => startSignup()} style={isMobile ? { padding: "8px 12px", fontSize: 13 } : {}}>Start Free Trial</button>
          </div>
        </nav>

        <main id="lp-main" tabIndex={-1} style={{ outline: "none" }}>
        {/* HERO — "Strata" (Direction C): layered tool cards drift behind the headline */}
        <section className="lp-hero" ref={heroRef}>
          <div className="lp-hero-glow" aria-hidden="true" />
          <div className="lp-hero-content lp-hero-stagger" style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: isMobile ? "48px 20px 32px" : "96px 32px 48px",
            textAlign: "center",
          }}>
          <div style={{
            display: "inline-block",
            fontSize: 12,
            fontWeight: 600,
            color: "var(--accent)",
            background: "rgba(212, 145, 92, 0.1)",
            border: "1px solid rgba(212, 145, 92, 0.25)",
            padding: "6px 14px",
            borderRadius: 999,
            marginBottom: 24,
            letterSpacing: 0.4,
          }}>
            BUILT FOR REAL ESTATE AGENTS
          </div>
          <h1 style={{
            fontFamily: "'Fraunces', serif",
            fontSize: isMobile ? 36 : 56,
            fontWeight: 500,
            lineHeight: 1.1,
            letterSpacing: "-1px",
            margin: "0 0 20px",
          }}>
            AI Writing Tools Built for<br />
            <em style={{ color: "var(--accent)", fontStyle: "italic" }}>Real Estate Agents</em>
          </h1>
          <p style={{
            fontSize: isMobile ? 16 : 19,
            color: "var(--text2)",
            maxWidth: 680,
            margin: "0 auto 32px",
            lineHeight: 1.5,
          }}>
            Save 5-10 hours a week on listings, lead emails, contract summaries, and admin work.
            Pays for itself with one extra deal a month.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              className="lp-nav-btn lp-cta-primary lp-magnetic"
              onClick={() => startSignup()}
              style={{ padding: "14px 28px", fontSize: 15 }}
            >
              Start Free Trial &rarr;
            </button>
            <button
              className="lp-nav-btn lp-cta-ghost lp-magnetic"
              onClick={onTryDemo}
              style={{ padding: "14px 28px", fontSize: 15 }}
            >
              Try Demo (No Signup)
            </button>
          </div>
          <div style={{
            marginTop: 18,
            fontSize: 13,
            color: "var(--text3)",
          }}>
            7-day free trial · No credit card required to explore the demo · Cancel anytime
          </div>
          </div>

          {/* Decorative tool cards — pure markup/SVG, zero raster load. aria-hidden (the
              real product is described in the Features section below). On mobile these
              reflow into a static stack below the CTAs (see appStyles .lp-card rules). */}
          <div className="lp-hero-cards" aria-hidden="true">
            {/* Card 1 — CMA pricing position */}
            <div className="lp-card lp-card-1">
              <div className="lp-card-head">
                <span className="lp-card-tag">CMA</span>
                <span className="lp-card-statusdot" />
              </div>
              <div className="lp-card-title">Pricing Position</div>
              <svg className="lp-card-bar" viewBox="0 0 216 30" role="presentation">
                <defs>
                  <linearGradient id="lpStrataBar" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#d4915c" stopOpacity="0.35" />
                    <stop offset="1" stopColor="#d4915c" />
                  </linearGradient>
                </defs>
                <rect x="0" y="10" width="216" height="6" rx="3" fill="#2a2a2a" />
                <rect x="0" y="10" width="150" height="6" rx="3" fill="url(#lpStrataBar)" />
                <circle cx="150" cy="13" r="6.5" fill="#d4915c" stroke="#0d0d0d" strokeWidth="2.5" />
                <text x="0" y="29" fontSize="8" fill="#8a8a8a" fontFamily="'Geist', sans-serif">$540k</text>
                <text x="216" y="29" fontSize="8" fill="#8a8a8a" textAnchor="end" fontFamily="'Geist', sans-serif">$720k</text>
              </svg>
              <div className="lp-comp-row"><span className="lp-comp-dot" /><span className="lp-comp-line" /><span className="lp-comp-price">$612k</span></div>
              <div className="lp-comp-row"><span className="lp-comp-dot" /><span className="lp-comp-line short" /><span className="lp-comp-price">$598k</span></div>
            </div>
            {/* Card 2 — MLS listing copy */}
            <div className="lp-card lp-card-2">
              <div className="lp-card-head">
                <span className="lp-card-tag">MLS Listing</span>
                <span className="lp-card-pill">AI</span>
              </div>
              <div className="lp-mls-line w1" />
              <div className="lp-mls-line w3" />
              <div className="lp-mls-line w2" />
              <div className="lp-mls-line w4" />
              <span className="lp-mls-copy">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" role="presentation">
                  <rect x="9" y="9" width="11" height="11" rx="2" />
                  <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                </svg>
                Copy
              </span>
            </div>
            {/* Card 3 — Property info */}
            <div className="lp-card lp-card-3">
              <div className="lp-card-head">
                <span className="lp-card-tag">Property Info</span>
                <span className="lp-card-statusdot" />
              </div>
              <div className="lp-info-grid">
                <div className="lp-info-cell"><span className="lp-info-label">Beds</span><span className="lp-info-value">4</span></div>
                <div className="lp-info-cell"><span className="lp-info-label">Baths</span><span className="lp-info-value">3</span></div>
                <div className="lp-info-cell"><span className="lp-info-label">Sq Ft</span><span className="lp-info-value">2,450</span></div>
                <div className="lp-info-cell"><span className="lp-info-label">Year</span><span className="lp-info-value">2018</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* SOCIAL PROOF MARQUEE — pure-CSS scroll, paused on hover, static under reduced motion */}
        <div className="lp-marquee" aria-hidden="true">
          <div className="lp-marquee-track">
            {[0, 1].map((dup) => (
              <span key={dup} style={{ display: "inline-flex", alignItems: "center" }}>
                {MARQUEE_ITEMS.map((item, i) => (
                  <span className="lp-marquee-item" key={i}>
                    {item}<span className="lp-marquee-sep">·</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* DEMO VIDEO */}
        {DEMO_VIDEO_URL && (
          <div style={{
            maxWidth: 960,
            margin: "0 auto",
            padding: isMobile ? "0 16px 56px" : "0 32px 80px",
          }}>
            <div style={{
              fontSize: 13,
              color: "var(--text3)",
              textAlign: "center",
              marginBottom: 16,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              fontWeight: 600,
            }}>
              See Pitcherly in action
            </div>
            <div style={{
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              borderRadius: 16,
              overflow: "hidden",
              border: "1px solid var(--border)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
            }}>
              {videoPlaying ? (
                <iframe
                  src={`${DEMO_VIDEO_URL.replace("www.youtube.com", "www.youtube-nocookie.com")}&autoplay=1`}
                  title="Pitcherly Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setVideoPlaying(true)}
                  aria-label="Play the Pitcherly demo video"
                  style={{
                    position: "absolute", inset: 0, width: "100%", height: "100%",
                    border: 0, padding: 0, cursor: "pointer",
                    background: `#0d0d0d center/cover no-repeat url(https://i.ytimg.com/vi/${DEMO_VIDEO_ID}/maxresdefault.jpg)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <span aria-hidden="true" style={{
                    width: 72, height: 72, borderRadius: "50%",
                    background: "rgba(13,13,13,0.66)", border: "1px solid rgba(255,255,255,0.28)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: 24, paddingLeft: 5,
                    transition: "transform 0.2s var(--ease-out-soft, ease), background 0.2s",
                  }}>▶</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* FEATURES */}
        <div style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: isMobile ? "16px 16px 56px" : "24px 32px 96px",
        }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h2 style={{
              fontFamily: "'Fraunces', serif",
              fontSize: isMobile ? 28 : 38,
              fontWeight: 500,
              margin: "0 0 12px",
            }}>
              Everything you need in one toolkit
            </h2>
            <p style={{ fontSize: 15, color: "var(--text2)", margin: 0 }}>
              Ten purpose-built tools for the work agents do every day.
            </p>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}>
            {FEATURES.map(f => (
              <div className="lp-feature reveal-on-scroll" key={f.title}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{f.title}</div>
                <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.5 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* PRICING */}
        <div id="landing-pricing" style={{
          maxWidth: 900,
          margin: "0 auto",
          padding: isMobile ? "32px 16px 56px" : "48px 32px 96px",
        }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h2 style={{
              fontFamily: "'Fraunces', serif",
              fontSize: isMobile ? 28 : 38,
              fontWeight: 500,
              margin: "0 0 12px",
            }}>
              Simple pricing, free trial
            </h2>
            <p style={{ fontSize: 15, color: "var(--text2)", margin: 0 }}>
              Start with 7 days free. Cancel anytime.
            </p>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "0.85fr 1fr 1fr",
            gap: 20,
            alignItems: "start",
          }}>
            {/* Lite */}
            <div style={{
              background: "var(--surface)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16,
              padding: 24,
            }}>
              <div style={{ fontSize: 13, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Lite</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 36, fontWeight: 500, margin: "12px 0 6px" }}>
                $19<span style={{ fontSize: 15, color: "var(--text3)", fontWeight: 400 }}>/month</span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 24px", color: "var(--text2)", fontSize: 13, lineHeight: 1.9 }}>
                <li>✓ 50 generations / month</li>
                <li>✓ All 10 writing tools</li>
                <li>✓ No property lookups</li>
              </ul>
              <button className="lp-nav-btn lp-cta-ghost" onClick={() => startSignup("lite")} style={{ width: "100%", padding: "12px 18px" }}>
                Start Free Trial &rarr;
              </button>
              <div style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", marginTop: 10 }}>
                7 days free · then $19/mo
              </div>
            </div>
            {/* Starter */}
            <div style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: 28,
            }}>
              <div style={{ fontSize: 13, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Starter</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 44, fontWeight: 500, margin: "12px 0 6px" }}>
                $49<span style={{ fontSize: 16, color: "var(--text3)", fontWeight: 400 }}>/month</span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 24px", color: "var(--text2)", fontSize: 14, lineHeight: 1.9 }}>
                <li>✓ 200 generations / month</li>
                <li>✓ All 10 writing tools</li>
                <li>✓ 10 verified property lookups / month</li>
                <li>✓ Market-data CMAs (live estimate + comps)</li>
                <li>✓ Contacts & CRM with CSV import</li>
                <li>✓ Email export (Gmail / Outlook)</li>
              </ul>
              <button className="lp-nav-btn lp-cta-ghost" onClick={() => startSignup("starter")} style={{ width: "100%", padding: "13px 20px" }}>
                Start Free Trial &rarr;
              </button>
              <div style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", marginTop: 10 }}>
                7 days free · then $49/mo
              </div>
            </div>
            {/* Pro */}
            <div style={{
              background: "var(--surface)",
              border: "1px solid var(--accent)",
              borderRadius: 16,
              padding: 28,
              position: "relative",
              boxShadow: "0 16px 40px rgba(212, 145, 92, 0.12)",
            }}>
              <div style={{
                position: "absolute",
                top: -10,
                right: 20,
                background: "var(--accent)",
                color: "#0d0d0d",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                padding: "4px 10px",
                borderRadius: 999,
                textTransform: "uppercase",
              }}>Most Popular</div>
              <div style={{ fontSize: 13, color: "var(--accent)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Pro</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 44, fontWeight: 500, margin: "12px 0 6px" }}>
                $99<span style={{ fontSize: 16, color: "var(--text3)", fontWeight: 400 }}>/month</span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "20px 0 24px", color: "var(--text2)", fontSize: 14, lineHeight: 1.9 }}>
                <li>✓ Unlimited generations</li>
                <li>✓ All 10 writing tools</li>
                <li>✓ 50 verified property lookups / month</li>
                <li>✓ Market-data CMAs (live estimate + comps)</li>
                <li>✓ Priority support</li>
                <li>✓ Early access to new features</li>
              </ul>
              <button className="lp-nav-btn lp-cta-primary" onClick={() => startSignup("pro")} style={{ width: "100%", padding: "13px 20px" }}>
                Start Free Trial &rarr;
              </button>
              <div style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", marginTop: 10 }}>
                7 days free · then $99/mo
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 28 }}>
            <button
              className="lp-nav-btn lp-cta-link"
              onClick={onTryDemo}
              style={{ fontSize: 14 }}
            >
              Just want to look around? Try the demo &rarr;
            </button>
          </div>
        </div>

        </main>

        {/* FOOTER */}
        <footer style={{
          borderTop: "1px solid var(--border)",
          padding: "32px 16px 24px",
          textAlign: "center",
          fontSize: 12,
          color: "var(--text3)",
        }}>
          <div style={{ marginBottom: 8 }}>
            &copy; 2026 Pitcherly &middot; Brea, CA 92821 &middot;{" "}
            <a href="mailto:support@pitcherly.com" style={{ color: "var(--text3)", textDecoration: "none" }}>support@pitcherly.com</a>
          </div>
          <div>
            <a href="/terms" style={{ color: "var(--text3)", textDecoration: "none" }}>Terms of Service</a>
            {" · "}
            <a href="/privacy" style={{ color: "var(--text3)", textDecoration: "none" }}>Privacy Policy</a>
          </div>
        </footer>
        {/* Hidden scroll target for pricing CTAs from elsewhere */}
        <div style={{ display: "none" }} onClick={scrollToPricing} />
      </div>
    </>
  );
}
